import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import profileApi from "../../../api/profile";
import { Loader, toast } from "../../../common/components/core";
import {
    useAuthErrorHandlerCreator,
    useModalWithData,
} from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import {
    GenericError,
    composeErrorHandlers,
} from "../../../utils/errorHandling";
import { CreateNewProfileFormValues } from "../components/CreateNewProfileForm";
import CreateProfileConfirmationContent from "../components/CreateProfileConfirmationContent";

const StyledModal = styled(Modal)`
    .modal-dialog {
        max-width: 640px;
    }
`;

type ConfirmCreateEntityHandler = {
    isSubmitting: boolean;
    handler: (formValues: CreateNewProfileFormValues) => void;
};

const useConfirmCreateProfileHandler = (): ConfirmCreateEntityHandler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const history = useHistory();

    const [show, hide] = useModalWithData<CreateNewProfileFormValues>(
        (formValues) => () => {
            const confirm = async () => {
                if (!formValues) {
                    hide();
                    return;
                }

                setSubmitting(true);
                try {
                    const newProfileRes = await profileApi.create(formValues);
                    hide();
                    toast("Profile Created");
                    history.push(
                        `/profile-management/profile-information/${newProfileRes.id}`,
                    );
                } catch (e) {
                    setSubmitting(false);
                    composeErrorHandlers(
                        onAuthError,
                        useGeneralError,
                    )(e as GenericError);
                }
                setSubmitting(false);
            };

            return (
                <ThemeProviderV2>
                <StyledModal show onHide={hide} backdrop="static" centered>
                    <Loader overlay isLoading={isSubmitting}>
                        <Modal.Header>
                            <h1 className="mb-0 rem-1-5">Profile Creation Confirmation</h1>
                        </Modal.Header>
                        <Modal.Body>
                            <CreateProfileConfirmationContent
                                data={formValues}
                            />
                        </Modal.Body>

                        <Modal.Footer className="d-flex justify-content-between px-3">
                            <Button variant="light.tertiary" onClick={hide}>
                                Cancel
                            </Button>
                            <Button variant="light.primary" onClick={confirm}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Loader>
                </StyledModal>
                </ThemeProviderV2>
            );
        },
        [isSubmitting, onAuthError],
    );

    return {
        isSubmitting,
        handler: show,
    };
};

export default useConfirmCreateProfileHandler;
